import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import CssBaseline from "@material-ui/core/CssBaseline";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Alert from "@material-ui/lab/Alert";
import Link from "@material-ui/core/Link";
import CircularProgress from "@material-ui/core/CircularProgress";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { Input } from "../../components/Formik";
import { useLazyQuery, API } from "../../api";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100vh",
    justifyContent: "center",
    alignItems: "center",
    backgroundImage: "linear-gradient(147deg, #000000 0%, #2c3e50 100%)",
  },
  paper: {
    padding: theme.spacing(4),
    background: "#fff",
    borderRadius: theme.spacing(1.5),
  },
  content: {
    width: "360px",
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  accountId: {
    fontWeight: "bold",
    margin: theme.spacing(2, 0, 2, 0),
  },
  button: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  alert: {
    marginBottom: theme.spacing(2),
  },
  item: {
    marginBottom: theme.spacing(2),
  },
}));

export default function Register({ history }) {
  const [register, { data, error, loading }] = useLazyQuery(API.REGISTER);

  const classes = useStyles();

  const [errorMessage, setErrorMessage] = React.useState(null);

  React.useEffect(() => {
    if (data) {
      window.location.href = "/validate?status=success&type=register";
    }
  }, [data]);

  React.useEffect(() => {
    if (error) {
      setErrorMessage(
        "Account with this email already exists. Would you like to"
      );
    }
  }, [error]);

  const handleSubmit = ({ password2, ...values }) => {
    register({ data: values });
  };

  const onLogin = () => {
    history.push("/login");
  };

  return (
    <div className={classes.root}>
      <CssBaseline />
      <Paper className={classes.paper} elevation={0}>
        <div className={classes.content}>
          {Boolean(errorMessage) && (
            <Alert severity="error" className={classes.alert}>
              {errorMessage}{" "}
              <Link color="inherit" onClick={onLogin}>
                sign in
              </Link>{" "}
              instead?
            </Alert>
          )}

          <Typography className={classes.title} variant="h6">
            Register New Business
          </Typography>

          <RegisterForm
            onSubmit={handleSubmit}
            initialValues={{
              businessName: "",
              firstName: "",
              lastName: "",
              email: "",
              password: "",
              password2: "",
            }}
            isLoading={loading}
          />

          <div className={classes.item}>
            <Link color="inherit" href="/login">
              Sign in to a different account
            </Link>
          </div>

          <Typography component="div" variant="caption">
            {"© "}
            {new Date().getFullYear()}, Code 369
          </Typography>
        </div>
      </Paper>
    </div>
  );
}

const RegisterForm = ({ initialValues, onSubmit, isLoading }) => {
  const classes = useStyles();

  const startIcon = isLoading && <CircularProgress size={16} color="#fff" />;

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      enableReinitialize={true}
      validateOnChange={true}
      validationSchema={Yup.object({
        businessName: Yup.string().required("Required"),
        firstName: Yup.string().required("Required"),
        lastName: Yup.string().required("Required"),
        email: Yup.string().email().required("Required"),
        password: Yup.string().required("Required"),
        password2: Yup.string()
          .oneOf([Yup.ref("password"), null], "Passwords must match")
          .required("Required"),
      })}
    >
      <Form>
        <Input name="businessName" label="Business Name" />
        <Input name="firstName" label="First Name" />
        <Input name="lastName" label="Last Name" />
        <Input name="email" label="Email" />
        <Input name="password" label="Password" type="password" />
        <Input name="password2" label="Confirm Password" type="password" />

        <Button
          disabled={isLoading}
          type="submit"
          className={classes.button}
          variant="contained"
          color="primary"
          startIcon={startIcon}
        >
          Register
        </Button>
      </Form>
    </Formik>
  );
};
